/**
 * Composant List_admin - Affiche et gère la liste des administrateurs
 * 
 * Fonctionnalités principales :
 * - Affichage paginé de la liste des administrateurs
 * - Recherche/filtrage des administrateurs
 * - Édition et suppression des administrateurs
 * - Gestion des permissions d'accès
 * - Interface responsive avec Material-UI
 * 
 * Props: Aucune (Ce composant est autonome)
 */
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  InputAdornment,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import { Scrollbar } from "../../../components/scrollbar";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../../hooks/use-auth";
import colors from './../../../../src/theme/colors.js';
const List_admin = () => {
  // ==================== ÉTATS DU COMPOSANT ====================
  const [admins, setAdmins] = useState([]); // Liste des administrateurs
  const [searchName, setSearchName] = useState(""); // Terme de recherche
  const [pagin, setPagin] = useState(0); // Page actuelle
  const [totalNumberOfAdmins, setTotalNumberOfAdmins] = useState(0); // Nombre total d'admins
  const [limit, setLimit] = useState(25); // Nombre d'éléments par page
  const [isSearching, setIsSearching] = useState(false); // État de recherche
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false); // Dialogue de suppression
  const [adminToDelete, setAdminToDelete] = useState(null); // Admin à supprimer
  const [hoveredId, setHoveredId] = useState(null); // ID de l'admin survolé
  const [permissions, setPermissions] = useState([]); // Permissions de l'utilisateur
  const { user } = useAuth(); // Utilisateur connecté
  const navigate = useNavigate();

  // ==================== FONCTIONS D'ACCÈS AUX PERMISSIONS ====================

  /**
   * Récupère les permissions de l'utilisateur connecté
   * @param {number} roles_id - ID du rôle de l'utilisateur
   */
  const getPermissionsByRole = async (roles_id) => {
    const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/permission-verification/get/`;
    const values = { roles_id };
    const accessToken = localStorage.getItem("accessToken");

    try {
      const resp = await fetch(url, {
        method: "POST",
        body: JSON.stringify(values),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!resp.ok) throw new Error(`HTTP error! Status: ${resp.status}`);

      const json = await resp.json();
      setPermissions(json.permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  /**
   * Vérifie si l'utilisateur a la permission de suppression
   * @returns {boolean} True si l'utilisateur a la permission
   */
  const hasDeletePermission = () => {
    return permissions.some(
      (permission) =>
        permission.component === "Administrateurs" && 
        permission.permission === "Supprimer"
    );
  };

  /**
   * Vérifie si l'utilisateur a la permission de mise à jour
   * @returns {boolean} True si l'utilisateur a la permission
   */
  const hasUpdatePermission = () => {
    return permissions.some(
      (permission) =>
        permission.component === "Administrateurs" && 
        permission.permission === "Mettre à jour"
    );
  };

  // ==================== FONCTIONS DE GESTION DES DONNÉES ====================

  /**
   * Récupère la liste des administrateurs depuis l'API
   */
  const fetchAdmins = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/admin/get`;
      const response = await fetch(url);

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      setAdmins(data.admin);
      setTotalNumberOfAdmins(data.admin.length);
    } catch (error) {
      console.error("Error fetching admins:", error);
      toast.error("Erreur lors du chargement des administrateurs");
    }
  }, []);

  /**
   * Filtre les administrateurs selon le terme de recherche
   */
  const handleSearch = useCallback(() => {
    const results = admins.filter((admin) => {
      const searchTerm = searchName.toLowerCase();
      return (
        (admin.firstname?.toLowerCase() || '').includes(searchTerm) ||
        (admin.lastname?.toLowerCase() || '').includes(searchTerm) ||
        (admin.email?.toLowerCase() || '').includes(searchTerm) ||
        (admin.role_name?.toLowerCase() || '').includes(searchTerm)
      );
    });
    setAdmins(results);
  }, [searchName, admins]);

  // ==================== GESTION DES ÉVÉNEMENTS ====================

  /**
   * Gère le changement de page dans la pagination
   * @param {object} event - Événement de changement
   * @param {number} newPage - Nouvelle page sélectionnée
   */
  const handlePageChange = (event, newPage) => {
    setPagin(newPage);
  };

  /**
   * Gère le changement du nombre d'éléments par page
   * @param {object} event - Événement de changement
   */
  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPagin(0);
  };

  /**
   * Gère l'édition d'un administrateur
   * @param {object} admin - Administrateur à éditer
   */
  const handleEdit = (admin) => {
    if (hasUpdatePermission()) {
      const tenant = localStorage.getItem('tenant');
      navigate(`/${tenant}/dashboard/admins/update/${admin.id}`, { 
        state: { admin } 
      });
    }
  };

  /**
   * Ouvre le dialogue de confirmation de suppression
   * @param {number} id - ID de l'administrateur à supprimer
   */
  const handleOpenDeleteDialog = (id) => {
    if (hasDeletePermission()) {
      setAdminToDelete(id);
      setOpenDeleteDialog(true);
    }
  };

  /**
   * Ferme le dialogue de suppression
   */
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setAdminToDelete(null);
  };

  /**
   * Supprime un administrateur après confirmation
   */
  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;

    try {
      const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/admin/delete/`;
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ id: adminToDelete }),
      });

      if (!response.ok) throw new Error(`Erreur HTTP! Status: ${response.status}`);

      const data = await response.json();

      if (data.msg === "Admin deleted successfully") {
        toast.success('Administrateur supprimé avec succès !');
        fetchAdmins();
      } else {
        toast.error(data.msg || "Erreur lors de la suppression.");
      }
    } catch (err) {
      console.error(err);
      toast.error('Une erreur est survenue lors de la suppression !');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // ==================== EFFETS ====================

  // Charge les permissions et les administrateurs au montage
  useEffect(() => {
    if (user && user.roles_id) {
      getPermissionsByRole(user.roles_id);
      fetchAdmins();
    }
  }, [user, fetchAdmins]);

  // Gère la recherche lorsque le terme dépasse 3 caractères
  useEffect(() => {
    if (searchName.length >= 3) {
      setIsSearching(true);
      handleSearch();
    } else if (isSearching) {
      fetchAdmins();
      setIsSearching(false);
    }
  }, [searchName, handleSearch, isSearching, fetchAdmins]);

  // ==================== RENDU ====================

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 11 }}>
      <Container maxWidth="xl">
        {/* En-tête */}
        <Box sx={{ mb: 5 }}>
          <Grid container justifyContent="space-between" spacing={3}>
            <Grid item>
              <Typography variant="h4" sx={{ fontFamily: "Montserrat" }}>
                Administrateurs
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Carte principale */}
        <Card>
          <Divider />
          
          {/* Barre de recherche */}
          <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", m: -1, p: 3 }}>
            <Box sx={{ m: 1, maxWidth: "100%", width: 450 }}>
              <TextField
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                placeholder="Rechercher un administrateur"
              />
            </Box>
          </Box>

          {/* Tableau des administrateurs */}
          <Scrollbar>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 3 }}>Nom</TableCell>
                  <TableCell sx={{ pl: 3 }}>Email</TableCell>
                  <TableCell align="center" sx={{ pl: 3 }}>Rôle</TableCell>
                  <TableCell sx={{ pl: 3 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.slice(pagin * limit, pagin * limit + limit).map((admin) => (
                  <TableRow
                    hover
                    key={admin.id}
                    onMouseEnter={() => setHoveredId(admin.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    {/* Cellule Nom */}
                    <TableCell sx={{ pl: 3 }}>
                      <Typography
                        component={hasUpdatePermission() ? RouterLink : "span"}
                        to={`/${localStorage.getItem('tenant')}/dashboard/admins/update/${admin.id}`}
                        state={{ admin }}
                        fontWeight="bold"
                        sx={{
                          color: colors.primary,
                          textDecoration: "none",
                          cursor: hasUpdatePermission() ? "pointer" : "default",
                          "&:hover": {
                            textDecoration: hasUpdatePermission() ? "underline" : "none",
                          },
                        }}
                      >
                        {`${admin.firstname} ${admin.lastname}`}
                      </Typography>
                    </TableCell>

                    {/* Cellule Email */}
                    <TableCell sx={{ pl: 3 }}>
                      <Typography sx={{ color: colors.primary }}>
                        {admin.email}
                      </Typography>
                    </TableCell>

                    {/* Cellule Rôle */}
                    <TableCell align="center" sx={{ pl: 3 }}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontSize: admin.role_name ? "0.875rem" : "0.75rem",
                          color: admin.role_name ? "inherit" : "text.secondary",
                        }}
                      >
                        {admin.role_name || "—"}
                      </Typography>
                    </TableCell>

                    {/* Cellule Actions */}
                    <TableCell sx={{ pl: 3 }}>
                      {hoveredId === admin.id && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {hasUpdatePermission() && (
                            <Tooltip title="Modifier">
                              <IconButton onClick={() => handleEdit(admin)} color="default">
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasDeletePermission() && (
                            <Tooltip title="Supprimer">
                              <IconButton 
                                onClick={() => handleOpenDeleteDialog(admin.id)} 
                                color="default"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Scrollbar>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalNumberOfAdmins}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={pagin}
            rowsPerPage={limit}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Lignes par page :"
          />
        </Card>

        {/* Dialogue de confirmation de suppression */}
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            Êtes-vous sûr de vouloir supprimer cet administrateur ?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Annuler
            </Button>
            <Button onClick={handleDeleteAdmin} color="error">
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default List_admin;